// Single source of truth for commission-service enums and label helpers.
//
// Every enum here is copied from the OpenAPI spec at
// https://commission-service.staging.aspin-inclusivity.com/docs-json
// (see docs/commissions-rewrite-step0.md section 2). Do not retype these per
// feature — import from here so a backend change is a one-line edit.
//
// ES5 only: no optional chaining.

import { formatCents, readCents } from './money.es6';

// Re-exported so the many existing call sites keep working while money handling
// lives in one place.
export { formatCents };

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Turns 'Failed_To_Update_Account' into 'Failed To Update Account'.
export function statusLabel(status) {
  return String(status).replace(/_/g, ' ');
}

// Turns 'sales_manager_agent' into 'Sales Manager Agent'.
// Exported because display sites need the SAME label the select options use: a
// value that reads "Sales Manager Agent" in a dropdown should not read
// "sales manager agent" in a table.
//
// It also absorbs casing drift in stored data. The service stores the literal
// "Agent" for an agent created without an explicit type, so real records hold
// both "agent" and "Agent"; titleising maps them to one label instead of showing
// operators two apparently different types.
export function enumLabel(value) {
  return String(value)
    .split(/[_\s]+/)
    .map(function(word) {
      return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
    })
    .join(' ');
}

// Builds [{ value, label }] option lists for selects from a bare value array,
// so no feature has to hand-write its own options.
export function toOptions(values, labelFor) {
  return values.map(function(value) {
    return {
      value: value,
      label: labelFor ? labelFor(value) : enumLabel(value)
    };
  });
}

// ─── Enums ───────────────────────────────────────────────────────────────────

export var POLICY_TYPES = [
  'Free',
  'Paid_Bundled',
  'Free_Agent',
  'Paid',
  'Embedded',
  'Family',
  'Individual'
];

export var PERIOD_TYPES = ['days', 'weeks', 'months', 'years'];

export var AGENT_TYPES = [
  'agent',
  'sales_manager_agent',
  'district_manager_agent',
  'mobile_money_agent',
  'company_agent'
];

// Mobile-money agents settle to an msisdn rather than a bank account number,
// so the bank form adapts on this.
export var MOBILE_MONEY_AGENT_TYPE = 'mobile_money_agent';

// What to send when the operator does not choose a type. Sending nothing is not
// an option: the service then stores the literal "Agent", which is not in the
// enum above, so `agent_type=Agent` is rejected 400 and `agent_type=agent` never
// matches it — the record drops out of the type filter permanently. Verified
// against staging on 2026-07-31.
export var DEFAULT_AGENT_TYPE = 'agent';

export var LANGUAGES = ['en', 'fr', 'rw', 'sw'];

var LANGUAGE_LABELS = {
  en: 'English',
  fr: 'French',
  rw: 'Kinyarwanda',
  sw: 'Swahili'
};

export function languageLabel(code) {
  return LANGUAGE_LABELS[code] || code;
}

export var REGISTRATION_CHANNELS = [
  'USSD',
  'WebClient',
  'Agent',
  'SMS',
  'SystemGenerated',
  'ApiClient'
];

// The service stores channel as free text, so this is the list operators pick
// from rather than an enum it enforces.
export var DISTRIBUTION_CHANNELS = [
  'Financial Advisor',
  'Independent Contractor'
];

// A stored value that is not one of the options stays selectable, so opening
// an older record does not show it as blank.
export function withCurrentValue(options, value) {
  if (!value) return options;
  for (var i = 0; i < options.length; i += 1) {
    if (options[i].value === value) return options;
  }
  return options.concat([{ value: value, label: value }]);
}

export var GENDERS = ['Male', 'Female'];

export var MARITAL_STATUSES = ['Single', 'Married', 'Divorced', 'Widowed'];

export var AML_RISKS = ['Low', 'Medium', 'High'];

export var ACADEMIC_QUALIFICATIONS = ['Degree', 'Diploma', 'Certificate'];

// Clawback rules select one or more policy statuses that trigger a clawback.
export var POLICY_STATUSES = [
  'Cancelled',
  'Unpaid',
  'Un_Confirmed',
  'Rejected',
  'Full_Coverage',
  'In_Arrears',
  'Non_Payment',
  'Not_Taken_Up',
  'Discarded',
  'Expired',
  'Pending'
];

export var DEFAULT_POLICY_STATUSES = ['Non_Payment'];

// Used as the `status` filter on transaction, payout and batch-payout lists.
export var TRANSACTION_STATUSES = [
  'Pending',
  'Paid',
  'Cancelled',
  'Failed',
  'Failed_Payment',
  'Failed_To_Update_Account',
  'Success',
  'In_Progress',
  'Completed',
  'Already_Calculated',
  'Already_Added_To_Account',
  'No_Premium_Received',
  'Not_Eligible'
];

// The calculation log (transaction requests) is filtered by these.
export var TRANSACTION_REQUEST_TYPES = ['Commission', 'Clawback', 'Payout'];

export var TRANSACTION_REQUEST_STATUSES = [
  'Pending',
  'In_Progress',
  'Success',
  'Failed',
  'Not_Eligible',
  'Already_Calculated',
  'No_Premium_Received'
];

var REASON_CODE_LABELS = {
  AGENT_INACTIVE: 'The agent was not active, so earned no commission.',
  PAYABLE_PERIOD_EXCEEDED:
    'The premium fell outside the periods the rule pays commission for.',
  PREMIUM_LIMIT_REACHED:
    'The rule had already paid for as many premiums as it allows.'
};

// Why a calculation produced nothing. Unknown codes fall back to the
// service's own feedback text.
export function reasonLabel(reasonCode, feedback) {
  if (reasonCode && REASON_CODE_LABELS[reasonCode]) {
    return REASON_CODE_LABELS[reasonCode];
  }
  return feedback || (reasonCode ? enumLabel(reasonCode) : '');
}

// Several statuses are opaque to non-engineers; these are surfaced as tooltips
// next to the badge.
var STATUS_TOOLTIPS = {
  Pending: 'Queued but not yet acted on.',
  Paid: 'Money has left the account and reached the agent.',
  Cancelled: 'Deliberately stopped before payment.',
  Failed: 'The operation did not complete. Safe to retry.',
  Failed_Payment:
    'The payment itself was rejected — the agent was not paid. Check the ' +
    'agent bank details, then retry the payout.',
  Failed_To_Update_Account:
    'The payment went out but the commission account balance was not ' +
    'updated. The agent has the money; the ledger needs reconciling. Do ' +
    'not retry the payout.',
  Success: 'Completed without error.',
  In_Progress: 'Currently being processed. No action needed.',
  Completed: 'Finished — nothing further to do.',
  Already_Calculated:
    'A commission for this premium was already worked out, so it was not ' +
    'calculated twice.',
  Already_Added_To_Account:
    'This amount was already credited to the commission account, so it was ' +
    'not credited twice.',
  No_Premium_Received:
    'No premium was received for the policy, so there was nothing to ' +
    'calculate a commission on.',
  Not_Eligible:
    'The calculation ran but no commission was due, for example because the ' +
    'agent was inactive. The reason is recorded in the calculation log.'
};

export function statusTooltip(status) {
  return STATUS_TOOLTIPS[status] || '';
}

// Statuses that end a batch payout's lifecycle — the status poller stops here.
export var TERMINAL_STATUSES = [
  'Paid',
  'Cancelled',
  'Failed',
  'Failed_Payment',
  'Failed_To_Update_Account',
  'Success',
  'Completed'
];

export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.indexOf(status) !== -1;
}

var STATUS_CHIP_COLORS = {
  pending: 'warning',
  paid: 'success',
  confirmed: 'success',
  success: 'success',
  completed: 'success',
  failed: 'error',
  failed_payment: 'error',
  failed_to_update_account: 'error',
  cancelled: 'default',
  processing: 'info',
  in_progress: 'info',
  already_calculated: 'info',
  already_added_to_account: 'info',
  no_premium_received: 'default',
  not_eligible: 'default'
};

export function statusChipColor(status) {
  return STATUS_CHIP_COLORS[String(status).toLowerCase()] || 'default';
}

// ─── Commission types ────────────────────────────────────────────────────────

// The 14-value backend enum, grouped for the select and with helper text per
// option. Grouping is presentational only; `value` is what goes on the wire.
export var COMMISSION_TYPE_GROUPS = [
  {
    group: 'Upfront only',
    options: [
      {
        value: 'Upfront',
        label: 'Upfront',
        help: 'Pay a share of the commission when the policy is taken out.'
      },
      {
        value: 'HundredPercentUpfront',
        label: 'Hundred Percent Upfront',
        help: 'Pay the entire commission at policy inception.'
      },
      {
        value: 'DebitOrderUpfront',
        label: 'Debit Order Upfront',
        help: 'Upfront commission for policies collected by debit order.'
      }
    ]
  },
  {
    group: 'Upfront then periodic',
    options: [
      {
        value: 'DebitOrderUpfrontPeriodic',
        label: 'Debit Order Upfront + Periodic',
        help: 'Upfront amount, then ongoing commission, for debit order.'
      },
      {
        value: 'MobileMoneyUpfrontPeriodic',
        label: 'Mobile Money Upfront + Periodic',
        help: 'Upfront amount, then ongoing commission, for mobile money.'
      },
      {
        value: 'CashUpfrontPeriodic',
        label: 'Cash Upfront + Periodic',
        help: 'Upfront amount, then ongoing commission, for cash.'
      },
      {
        value: 'POSUpfrontPeriodic',
        label: 'POS Upfront + Periodic',
        help: 'Upfront amount, then ongoing commission, for POS collections.'
      },
      {
        value: 'FixedUpfrontPeriodic',
        label: 'Fixed Upfront + Periodic',
        help: 'A fixed upfront amount followed by fixed ongoing amounts.'
      }
    ]
  },
  {
    group: 'Per premium',
    options: [
      {
        value: 'PerPremium',
        label: 'Per Premium',
        help: 'Calculate a commission on each premium received.'
      },
      {
        value: 'FixedPerPremium',
        label: 'Fixed Per Premium',
        help: 'A fixed amount for each premium received.'
      },
      {
        value: 'PerPremiumGivenPolicyDuration',
        label: 'Per Premium Given Policy Duration',
        help:
          'Commission per premium, varying by which period of the policy ' +
          'the premium falls in.'
      }
    ]
  },
  {
    group: 'Fixed and periodic',
    options: [
      {
        value: 'Fixed',
        label: 'Fixed',
        help: 'A single fixed commission amount.'
      },
      {
        value: 'FixedUpfront',
        label: 'Fixed Upfront',
        help: 'A fixed amount paid once, at inception.'
      },
      {
        value: 'Periodic',
        label: 'Periodic',
        help: 'Ongoing commission on a schedule, with no upfront amount.'
      }
    ]
  }
];

// Flat list for validation and filters.
export var COMMISSION_TYPES = COMMISSION_TYPE_GROUPS.reduce(function(all, g) {
  return all.concat(
    g.options.map(function(option) {
      return option.value;
    })
  );
}, []);

var COMMISSION_TYPE_OPTIONS = COMMISSION_TYPE_GROUPS.reduce(function(map, g) {
  g.options.forEach(function(option) {
    map[option.value] = option;
  });
  return map;
}, {});

export function commissionTypeHelp(value) {
  var option = COMMISSION_TYPE_OPTIONS[value];
  return option ? option.help : '';
}

// Falls back to the raw value, so a type the service adds before this list
// catches up still shows rather than disappearing.
export function commissionTypeLabel(value) {
  var option = COMMISSION_TYPE_OPTIONS[value];
  return option ? option.label : value || '';
}

// ─── MathJSON ────────────────────────────────────────────────────────────────

// Operators the formula builder accepts: everything the CortexJS Compute Engine
// supports (Gap 9, resolved — the library is the contract, not the four
// operators the spec examples happen to use).
//
// This list is DERIVED FROM THE LIBRARY, not hand-written. It was produced by
// loading @cortex-js/compute-engine and keeping every candidate name for which
// `ce.lookupDefinition(name)` returns a definition. To regenerate after a
// compute-engine upgrade, run that check again rather than editing by hand.
//
// At runtime the builder can consult the loaded engine directly — see
// isOperatorKnownToEngine in lib/mathjson/computeEngineLoader.es6.js. This static
// list is the offline answer, so validation works before the engine has loaded
// and so the normalization tests need no browser.
export var SUPPORTED_MATHJSON_OPERATORS = [
  // Arithmetic
  'Add', 'Subtract', 'Multiply', 'Divide', 'Negate', 'Rational', 'Power',
  'Sqrt', 'Root', 'Square', 'Exp', 'Ln', 'Log', 'Lb', 'Lg',
  // Rounding and sign
  'Abs', 'Sign', 'Floor', 'Round', 'Truncate', 'Mod', 'Chop',
  // Aggregate and combinatorial
  'Min', 'Max', 'Sum', 'Product', 'Mean', 'Median', 'Variance',
  'StandardDeviation', 'GCD', 'LCM', 'Factorial', 'Gamma', 'Binomial',
  // Comparison
  'Equal', 'NotEqual', 'Less', 'LessEqual', 'Greater', 'GreaterEqual',
  // Logic and conditionals
  'And', 'Or', 'Not', 'Xor', 'Implies', 'If', 'Which', 'Boole',
  // Trigonometry
  'Sin', 'Cos', 'Tan', 'Arcsin', 'Arccos', 'Arctan', 'Arctan2', 'Sinh',
  'Cosh', 'Tanh',
  // Structural
  'Delimiter', 'Sequence', 'InvisibleOperator', 'List', 'Tuple', 'Hold',
  'Evaluate', 'Simplify', 'N', 'Nothing', 'Error', 'String',
  // Constants
  'Pi', 'ExponentialE', 'ImaginaryUnit', 'NaN', 'True', 'False',
  'Complex'
];

// The commission formula chain must produce this name, and the clawback chain
// must produce its own terminal name. Save is blocked without them.
export var REQUIRED_COMMISSION_FORMULA = 'commission_amount_payable';
export var REQUIRED_CLAWBACK_FORMULA = 'clawback_amount';

// ─── Agent labels ────────────────────────────────────────────────────────────

// "Name (CODE)". GUIDs are database identifiers and are never shown to users,
// so this never falls back to one.
function nameWithCode(name, code) {
  if (name && code) return name + ' (' + code + ')';
  return name || code || '';
}

export function agentLabel(agent) {
  if (!agent) return '';
  return nameWithCode(
    agent.full_name || agent.first_name || '',
    agent.external_identifier || ''
  );
}

export function agencyLabel(agency) {
  if (!agency) return '';
  return nameWithCode(agency.agency_name || '', agency.agency_code || '');
}

// The agency an agent belongs to, from the snapshot the service stores on the
// agent; falls back to the legacy broker_company string.
export function agentAgencyLabel(agent) {
  if (!agent) return '';
  return nameWithCode(
    agent.agency_company_name || '',
    agent.agency_code || ''
  ) || agent.broker_company || '';
}

// ─── Amounts ─────────────────────────────────────────────────────────────────

// Non-cents amount fields, kept for payloads that predate the *_in_cents
// convention. Cents fields are handled by readCents in money.es6.
var PLAIN_AMOUNT_FIELDS = ['commission_amount', 'amount', 'net_amount'];

export function resolveAmount(txn) {
  if (!txn) return null;

  var cents = readCents(txn);
  if (cents !== null) return formatCents(cents);

  for (var i = 0; i < PLAIN_AMOUNT_FIELDS.length; i += 1) {
    var value = txn[PLAIN_AMOUNT_FIELDS[i]];
    if (value !== undefined && value !== null) return value;
  }
  return null;
}
